import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, ExternalLink, FilePlus2, Pencil, Trash2 } from "lucide-react";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageEditor } from "@/components/admin/website/PageEditor";

export const PagesManager = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [pages, setPages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", slug: "" });

  const load = useCallback(() => axios.get(`${API}/admin/website/pages`, { headers }).then((r) => setPages(r.data)), [headers]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const r = await axios.post(`${API}/admin/website/pages`, form, { headers });
      setForm({ title: "", slug: "" }); setEditing(r.data.page_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't create page"); }
  };
  const duplicate = async (p) => { await axios.post(`${API}/admin/website/pages/${p.page_id}/duplicate`, {}, { headers }); toast.success("Duplicated as draft"); load(); };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    await axios.delete(`${API}/admin/website/pages/${p.page_id}`, { headers }); load();
  };

  const publishAll = async () => {
    const drafts = pages.filter((p) => p.status !== "published");
    if (!drafts.length || !window.confirm(`Publish ${drafts.length} draft page(s) now?`)) return;
    for (const p of drafts) await axios.post(`${API}/admin/website/pages/${p.page_id}/publish`, {}, { headers });
    toast.success(`${drafts.length} page(s) published`); load();
  };

  if (editing) return <PageEditor pageId={editing} onBack={() => { setEditing(null); load(); }} />;
  return (
    <div className="space-y-4" data-testid="pages-manager">
      <div className="flex flex-wrap gap-2 items-end rounded-xl border bg-white p-3">
        <label className="text-xs font-semibold text-slate-600 flex-1 min-w-[160px]">New page title<Input className="mt-1" value={form.title} onChange={(e) => setForm({ title: e.target.value, slug: form.slugTouched ? form.slug : e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") })} data-testid="new-page-title" /></label>
        <label className="text-xs font-semibold text-slate-600 flex-1 min-w-[160px]">Web address<Input className="mt-1" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""), slugTouched: true })} data-testid="new-page-slug" /></label>
        <Button onClick={create} disabled={!form.slug} data-testid="new-page-create"><FilePlus2 className="w-4 h-4 mr-1" />Create page</Button>
        <Button variant="outline" onClick={publishAll} disabled={!pages.some((p) => p.status !== "published")} data-testid="pages-publish-all">Publish all drafts</Button>
      </div>
      <div className="rounded-xl border bg-white divide-y">
        {!pages.length && <p className="p-6 text-sm text-muted-foreground text-center">No pages yet - create one or use Import to copy your current website.</p>}
        {pages.map((p) => (
          <div key={p.page_id} className="flex flex-wrap items-center gap-2 px-4 py-3" data-testid={`page-row-${p.slug}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{p.title}</p>
              <p className="text-xs text-slate-500">/{p.slug === "home" ? "" : p.slug} · updated {new Date(p.updated_at).toLocaleDateString()}</p>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${p.status === "published" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
            {p.status === "published" && <Button size="icon" variant="ghost" asChild><a href={p.slug === "home" ? "/" : `/${p.slug}`} target="_blank" rel="noreferrer" title="View"><ExternalLink className="w-4 h-4" /></a></Button>}
            <Button size="icon" variant="ghost" onClick={() => duplicate(p)} title="Duplicate" data-testid={`page-duplicate-${p.slug}`}><Copy className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(p)} title="Delete" data-testid={`page-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></Button>
            <Button size="sm" onClick={() => setEditing(p.page_id)} data-testid={`page-edit-${p.slug}`}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
          </div>
        ))}
      </div>
    </div>
  );
};
